/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericFooterComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";

import { SplitButton } from "VSSUI/SplitButton";
import { Button } from "VSSUI/Button";
import { IMenuItem } from "VSSUI/Menu";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Link } from "VSSUI/Link";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ServiceEndpointTypes, EndpointAuthorizationSchemes } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";

export interface GenericFooterComponentProps {
    isUpdateForm: boolean;
    enableSave: boolean;
    enableSaveAsDraft?: boolean;
    enableNext?: boolean;
    serviceEndpoint?: ServiceEndpoint;
    isVerifiable?: boolean;
    verifyInProgress?: boolean;
    saveInProgress?: boolean;
    setupInProgress?: boolean;
    showSaveButton?: boolean;
    showBackButton?: boolean;
    showNextButton?: boolean;
    showSaveAsDraftButton?: boolean;
    onVerifyAndSaveButtonClick?: () => void;
    onSaveButtonClick?: () => void;
    onBackButtonClick?: () => void;
    onNextButtonClick?: () => void;
}

export class GenericFooterComponent extends VssComponent<GenericFooterComponentProps> {
    constructor(props: GenericFooterComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._canVerifyButtonBeFocused = false;
    }

    public render(): React.ReactNode {
        if (this.props.isVerifiable && this.props.verifyInProgress) {
            this._canVerifyButtonBeFocused = true;
        }
        const loadingLabel = this.props.setupInProgress
            ? Resources.SettingUpSpinnerLabel
            : this.props.saveInProgress ? Resources.SavingSpinnerLabel : Resources.VerifyingSpinnerLabel;
        return (<React.Fragment>
            <div className="connections-editor-panel-footer">
                <div className="connections-editor-panel-generic-footer-left">
                    <Link
                        className="links-connections"
                        href={this._getLearnMoreLink()}
                        target="_blank"
                        ariaLabel={Resources.LearnMoreTextAriaLabel}
                    >
                        {Resources.LearnMoreText}
                    </Link>
                    <Link
                        className="links-connections link-secondary"
                        href={this._getTroubleshootLink()}
                        target="_blank"
                        ariaLabel={Resources.TroubleshootingTextAriaLabel}
                    >
                        {Resources.TroubleshootingText}
                    </Link>
                </div>
                <div className="connections-editor-panel-footer-right">
                    {this.props.setupInProgress || this.props.saveInProgress || this.props.verifyInProgress ?
                        <Spinner ariaLive="assertive" label={loadingLabel} className="loading-spinner" size={SpinnerSize.medium} />
                        :
                        <React.Fragment>
                            {this.props.showBackButton && this._getBackButton()}
                            {this.props.showSaveAsDraftButton && this._getSaveAsDraftButton()}
                            {this.props.showSaveButton && this._getSaveButton()}
                            {this.props.showNextButton && this._getNextButton()}
                        </React.Fragment>
                    }
                </div>
            </div>
        </React.Fragment>
        );
    }

    private _getSaveButton(): React.ReactNode {
        if (this.props.isVerifiable) {
            if (this.props.showSaveAsDraftButton) {
                 return (
                    <Button
                        disabled={!this.props.enableSave}
                        primary
                        text={Resources.VerifyAndSaveText}
                        onClick={this.props.onVerifyAndSaveButtonClick}
                        />
                );
            } else {
                const saveButtonRef = (ref: Button) => {
                    if (ref && this.props.enableSave && this._canVerifyButtonBeFocused) {
                        ref.focus();
                        this._canVerifyButtonBeFocused = false;
                    }
                };

                return (
                    <SplitButton
                        disabled={!this.props.enableSave}
                        primary={true}
                        buttonProps={{
                            text: Resources.VerifyAndSaveText,
                            onClick: this.props.onVerifyAndSaveButtonClick
                        }}
                        menuButtonProps={{
                            contextualMenuProps: {
                                menuProps: {
                                    id: "1",
                                    items: this._menuItems
                                }
                            }
                        }}
                        buttonRef={saveButtonRef}
                    />
                );
            }
        } else {
            return (
                <Button
                    disabled={!this.props.enableSave}
                    primary
                    text={Resources.SaveServiceConnection}
                    onClick={this.props.onSaveButtonClick}
                />
            );
        }
    }

    private _getBackButton(): React.ReactNode {
        return (
            <Button
                className="back-button"
                text={this.props.isUpdateForm ? Resources.Cancel : Resources.BackButtonText}
                onClick={this.props.onBackButtonClick}
            />
        );
    }

    private _getSaveAsDraftButton(): React.ReactNode {
        return (
            <Button
                className="back-button"
                disabled={!this.props.enableSaveAsDraft}
                text={Resources.SaveAsDraftText}
                onClick={this.props.onSaveButtonClick}
            />
        );
    }

    private _getNextButton(): React.ReactNode {
        const { enableNext = true } = this.props;
        return (
            <Button
                className="next-button"
                primary
                disabled={!enableNext}
                text={"Next"}
                onClick={this.props.onNextButtonClick}
            />
        );
    }

    private _menuItems: IMenuItem[] = [
        {
            id: "save",
            text: Resources.SaveText,
            onActivate: this.props.onSaveButtonClick
        },
    ];

    private _getLearnMoreLink(): string {
        switch (this.props.serviceEndpoint && this.props.serviceEndpoint.type && this.props.serviceEndpoint.type.toLocaleLowerCase()) {
            case ServiceEndpointTypes.AzureRM.toLocaleLowerCase():
                if (this.props.serviceEndpoint.authorization.scheme === EndpointAuthorizationSchemes.WorkloadIdentityFederation) {
                    return EndpointsEditorPanelUtils.AZURE_RM_WORKLOAD_IDENTITY_FEDERATION_LEARN_MORE_LINK;
                }
                return EndpointsEditorPanelUtils.AZURE_RM_LEARN_MORE_LINK;
            case ServiceEndpointTypes.Docker.toLocaleLowerCase():
                if (this.props.serviceEndpoint.data.registrytype.toLocaleLowerCase() === "acr") {
                    
                    if (this.props.serviceEndpoint.authorization.scheme === EndpointAuthorizationSchemes.ManagedServiceIdentity) {
                        return EndpointsEditorPanelUtils.DOCKER_TO_AZURE_CONTAINER_REGISTRY_LEARN_MORE_LINK;
                    }

                    return EndpointsEditorPanelUtils.DOCKER_LEARN_MORE_LINK;
                }
               
                return EndpointsEditorPanelUtils.SERVICE_ENDPOINTS_LEARN_MORE_LINK;
            default: return EndpointsEditorPanelUtils.SERVICE_ENDPOINTS_LEARN_MORE_LINK;
        }
    }

    private _getTroubleshootLink(): string {
        switch (this.props.serviceEndpoint && this.props.serviceEndpoint.type && this.props.serviceEndpoint.type.toLocaleLowerCase()) {
            case ServiceEndpointTypes.AzureRM.toLocaleLowerCase(): {
                if (this.props.serviceEndpoint.authorization.scheme === EndpointAuthorizationSchemes.WorkloadIdentityFederation) {
                    return EndpointsEditorPanelUtils.AZURE_RM_WORKLOAD_IDENTITY_FEDERATION_TROUBLESHOOT_LINK;
                }
                return EndpointsEditorPanelUtils.AZURE_RM_TROUBLESHOOT_LINK;
            }
            default: return EndpointsEditorPanelUtils.SERVICE_ENDPOINTS_TROUBLESHOOT_LINK;
        }
    }

    private _canVerifyButtonBeFocused: boolean;
}
